import { useCallback } from 'react'
import { useI18n, type MessageKey } from '@/i18n'
import { en } from '@/i18n/en'
import type { GateConfig, RosterReason } from '@/offline'

function isKey(k: string): k is MessageKey {
  return k in en
}

/**
 * Labels for codes that come from the roster (red-line reasons, dispute reasons, crops) or the
 * server (hold reasons, payout status): the roster's own hi/en text when sent, else our dictionary
 * (`gate.code.<CODE>`), else the code itself.
 */
export function useCodeLabel() {
  const { t, locale } = useI18n()
  return useCallback(
    (r: RosterReason | string | null | undefined): string => {
      if (!r) return '—'
      const code = typeof r === 'string' ? r : r.code
      if (typeof r !== 'string') {
        const own = locale === 'hi' ? (r.hi ?? r.en) : (r.en ?? r.hi)
        if (own) return own
      }
      const key = `gate.code.${code}`
      return isKey(key) ? t(key) : code.replace(/_/g, ' ').toLowerCase()
    },
    [t, locale],
  )
}

export function useCropLabel(config: GateConfig | undefined) {
  const { t, locale } = useI18n()
  return useCallback(
    (cropId: string | null | undefined): string => {
      if (!cropId) return '—'
      const c = config?.crops.find((x) => x.crop_id === cropId)
      const own = locale === 'hi' ? (c?.name_hi ?? c?.name_en) : (c?.name_en ?? c?.name_hi)
      if (own) return own
      const key = `gate.crop.${cropId}`
      return isKey(key) ? t(key) : cropId
    },
    [config, t, locale],
  )
}
